import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES, SITE } from "@/lib/site";
import { SERVICE_FAQS } from "@/lib/faqs";
import FAQ from "@/components/FAQ";
import useSEO from "@/hooks/useSEO";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find(s => s.slug === slug);
  useSEO({
    title: service ? `${service.title} in Toronto & GTA` : "Service",
    description: service ? `${service.intro} Free quotes and mockups across Toronto, Mississauga, Brampton, Vaughan, Markham, and Scarborough.` : undefined,
    path: `/services/${slug}`,
    image: service?.image,
  });
  useEffect(() => {
    if (!service) return;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "service-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": service.title,
      "description": service.intro,
      "image": service.image,
      "provider": {
        "@type": "LocalBusiness",
        "name": SITE.name,
        "telephone": SITE.phone,
        "url": SITE.url,
      },
      "areaServed": SITE.areas.map(a => ({ "@type": "City", "name": a })),
      "url": `${SITE.url}/services/${service.slug}`,
    });
    document.head.appendChild(ld);
    // BreadcrumbList — Home › Services › <this service>. Google renders this
    // as a rich path under the title in search results.
    const bc = document.createElement("script");
    bc.type = "application/ld+json";
    bc.id = "service-breadcrumb-jsonld";
    bc.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
        { "@type": "ListItem", position: 2, name: "Services", item: `${SITE.url}/services` },
        { "@type": "ListItem", position: 3, name: service.title, item: `${SITE.url}/services/${service.slug}` },
      ],
    });
    document.head.appendChild(bc);
    return () => { ld.remove(); bc.remove(); };
  }, [service]);
  if (!service) return <Navigate to="/services" replace />;
  const idx = SERVICES.findIndex(s => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <>
      <section className="relative pt-36 pb-24 overflow-hidden" data-testid={`service-detail-${slug}`}>
        <div className="absolute inset-0">
      <img src={service.image} alt={`${service.title} — ${service.short} project by Toronto Custom Signs & Wraps`} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-ink-950/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Link to="/services" className="text-[12px] uppercase tracking-[0.2em] text-white/60 hover:text-gold" data-testid="back-to-services">← All services</Link>
          <div className="eyebrow gold-bar mt-6">0{idx+1} · {service.short}</div>
          <h1 className="font-display text-5xl md:text-7xl mt-4 tracking-tight max-w-4xl">{service.title}</h1>
          <p className="text-white/70 mt-7 max-w-2xl text-lg leading-relaxed">{service.intro}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-gold" data-testid="service-detail-quote">Request a quote <ArrowUpRight size={14}/></Link>
            <Link to="/gallery" className="btn-ghost">View gallery</Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-ink-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">What's included</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 tracking-tight">Capabilities</h2>
            <p className="text-white/60 mt-5">A focused set of capabilities — every detail handled in-house.</p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {service.includes.map(item => (
              <div key={item} className="card-dark p-5 flex items-start gap-3" data-testid={`capability-${item.replace(/\s/g,'-').toLowerCase()}`}>
                <Check size={16} className="text-gold mt-1"/>
                <span className="text-white/85">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQ
        items={SERVICE_FAQS[slug] || []}
        id={`faq-${slug}`}
        title={`${service.title} — common questions`}
        eyebrow="Questions"
      />

      <section className="py-20 bg-ink-950">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-6 border border-white/10 p-10">
          <div>
            <div className="eyebrow">Up next</div>
            <Link to={`/services/${next.slug}`} className="font-display text-3xl md:text-4xl mt-2 inline-flex items-center gap-3 hover:text-gold transition-colors" data-testid="next-service-link">
              {next.title} <ArrowUpRight size={22}/>
            </Link>
          </div>
          <Link to="/contact" className="btn-gold">Start your project <ArrowUpRight size={14}/></Link>
        </div>
      </section>
    </>
  );
}
